import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, catchError, map, of } from 'rxjs';

import { setUser } from '@lib/core/stores/user/user.actions';
import { selectUser } from '@lib/core/stores/user/user.selectors';
import { User } from '@lib/core/stores/user/user.state.interface';
import { API_URL } from '@lib/core/tokens/api.tokens';

import { SystemUserProfile } from '../models/system-user.interface';
import { SystemUsersService } from './system-users.service';

export interface HealthManagerProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

export enum PortalType {
  HEALTH_MANAGER = 'health-manager',
  DOCTORS = 'doctors',
  ADMIN = 'admin',
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileResolverService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly apiUrl = inject(API_URL);
  private readonly systemUsersService = inject(SystemUsersService);

  resolveUserProfile(portalType: PortalType): Observable<User> {
    switch (portalType) {
      case PortalType.HEALTH_MANAGER:
        return this.resolveHealthManagerProfile();
      case PortalType.DOCTORS:
        return this.resolveDoctorProfile();
      case PortalType.ADMIN:
        return this.resolveAdminProfile();
      default:
        return this.createDefaultProfile();
    }
  }

  private resolveHealthManagerProfile(): Observable<User> {
    return this.http.get<HealthManagerProfile>(`${this.apiUrl}/profile`).pipe(
      map(profile => this.toUser(profile)),
      catchError(() => this.createDefaultProfile(PortalType.HEALTH_MANAGER))
    );
  }

  private resolveDoctorProfile(): Observable<User> {
    const currentUserInfo = this.getCurrentUserInfo();

    return this.systemUsersService.getAll().pipe(
      map((users: SystemUserProfile[]) => {
        let foundUser: SystemUserProfile | undefined;

        if (currentUserInfo?.email) {
          foundUser = users.find(user => user.email === currentUserInfo.email);
        }

        if (currentUserInfo?.id) {
          foundUser = foundUser || users.find(user => user.id === currentUserInfo.id || user.userId === currentUserInfo.id);
        }

        if (foundUser) {
          return this.systemUserToUser(foundUser);
        }

        return this.buildDefaultUser(PortalType.DOCTORS);
      }),
      catchError(() => this.createDefaultProfile(PortalType.DOCTORS))
    );
  }

  private resolveAdminProfile(): Observable<User> {
    return this.resolveDoctorProfile();
  }

  private getCurrentUserInfo(): { id?: string; email?: string } | null {
    try {
      const user = this.store.selectSignal(selectUser)();

      if (user) {
        return {
          id: user.id || user.userId,
          email: user.email,
        };
      }

      const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
      if (token) {
        const payload = this.decodeJwtPayload(token);
        return {
          id: payload.sub || payload.userId || payload.id,
          email: payload.email,
        };
      }

      return null;
    } catch (error) {
      console.error('Error getting current user info:', error);
      return null;
    }
  }

  private decodeJwtPayload(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) throw new Error('Invalid JWT token format');

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding JWT payload:', error);
      return {};
    }
  }

  private toUser(profile: HealthManagerProfile): User {
    return {
      id: profile.id,
      userId: profile.id,
      fullName: profile.fullName || this.buildFullName(profile.firstName, profile.lastName) || 'Health Manager',
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      middleName: '',
      email: profile.email || '',
      phoneNumber: '',
      phoneCode: '',
      gender: '',
      birthDate: '',
    };
  }

  private systemUserToUser(systemUser: SystemUserProfile): User {
    return {
      id: systemUser.id,
      userId: systemUser.userId,
      fullName: systemUser.fullName,
      firstName: systemUser.firstName,
      lastName: systemUser.lastName,
      middleName: systemUser.middleName,
      email: systemUser.email,
      phoneNumber: systemUser.phoneNumber,
      phoneCode: systemUser.phoneCode,
      gender: systemUser.gender,
      birthDate: systemUser.birthDate,
    };
  }

  private buildFullName(firstName?: string, lastName?: string): string {
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '';
  }

  private buildDefaultUser(portalType?: PortalType): User {
    const currentUserInfo = this.getCurrentUserInfo();
    const defaultNames: Record<string, string> = {
      [PortalType.DOCTORS]: 'Лікар lilo',
      [PortalType.HEALTH_MANAGER]: 'Менеджер здоров`я',
      [PortalType.ADMIN]: 'Адмін lilo',
    };
    const defaultName = portalType ? defaultNames[portalType] || 'User' : 'User';

    return {
      id: currentUserInfo?.id || 'unknown',
      userId: currentUserInfo?.id || 'unknown',
      fullName: defaultName,
      firstName: '',
      lastName: '',
      middleName: '',
      email: currentUserInfo?.email || '',
      phoneNumber: '',
      phoneCode: '',
      gender: '',
      birthDate: '',
    };
  }

  private createDefaultProfile(portalType?: PortalType): Observable<User> {
    return of(this.buildDefaultUser(portalType));
  }

  /**
   * Resolves and sets user profile in store
   */
  resolveAndSetUserProfile(portalType: PortalType): Observable<User> {
    return this.resolveUserProfile(portalType).pipe(
      map(user => {
        this.store.dispatch(setUser({ user }));

        return user;
      })
    );
  }
}
